// src/routes/__root.tsx
/// <reference types="vite/client" />

import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { createRootRoute, Outlet, ScrollRestoration } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import { Guide } from '@/components/Guide';
import { MicrosoftClarity, Umami } from '@/components/Metrics';
import RouterProgressBar from '@/components/RouterProgress';
import { useLoadAllTranslations } from '@/features/app/hooks/useLoadAllTranslations';
import type { IServerEnv } from '@/lib/server-env';
import '@/lib/i18n/config';
import type { IUser } from '@/sdk';
import { colors } from '@/themes/colors';
import { getColorsCssVariablesText } from '@/themes/utils';
import { AppProviders } from '../AppProviders';

import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { TanStackDevtools } from '@tanstack/react-devtools'

dayjs.extend(utc);
dayjs.extend(timezone);
extendZodWithOpenApi(z);

// TODO: Get user and env from route context/loader once implemented
const RootComponent = () => {
  // Load all translations on app start (client-side)
  useLoadAllTranslations();

  // TODO: Get user from route context
  const user: IUser | undefined = undefined;
  const env: IServerEnv = {};

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    }
  }, [user]);

  return (
    <>
      <style>{getColorsCssVariablesText(colors)}</style>
      <AppProviders env={env}>
        <MicrosoftClarity clarityId={env.microsoftClarityId} user={user} />
        <Umami umamiWebSiteId={env.umamiWebSiteId} umamiUrl={env.umamiUrl} user={user} />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.version="${import.meta.env.VITE_BUILD_VERSION ?? (import.meta.env.DEV ? 'develop' : 'production')}";
              window.__TE__=${JSON.stringify(env)};
            `,
          }}
        />
        <ScrollRestoration />
        <Outlet />
        {user && <Guide user={user} />}
        <RouterProgressBar />
      </AppProviders>
      <TanStackDevtools
        config={{
          position: 'bottom-right',
        }}
        plugins={[
          {
            name: 'Tanstack Router',
            render: <TanStackRouterDevtoolsPanel />,
          },
        ]}
      />
    </>
  );
};

export const Route = createRootRoute({
  component: RootComponent,
});

